import { useMutation } from "@tanstack/react-query";
import { useLingui } from "@lingui/react";
import { toast } from "sonner";

/**
 * Downloads a phone-book export (.vcf, vCard 3.0) of staff or parents, for
 * import into phone address books.
 *
 * The endpoints stream `text/vcard` rather than JSON and take the IDs in a
 * POST body (a selection can hold hundreds of IDs, too long for a query
 * string), so they use a raw `fetch` with a bearer token instead of the
 * generated OpenAPI client — the same shape `useSettlingInPdf` uses.
 */

/** Which phone book to export, matching the API path segment. */
export type VCardExportKind = "employees" | "contacts";

interface DownloadedFile {
  blob: Blob;
  filename: string;
}

async function downloadVCard(kind: VCardExportKind, ids: string[]): Promise<DownloadedFile> {
  const { getAuthToken } = await import("@/api/auth-helper");
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || "";

  const response = await fetch(`${baseUrl}/api/v1/${kind}/export/vcf`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ message: "Download failed" }));
    throw new Error(errorBody.message || `HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const fallbackName = kind === "employees" ? "staff" : "parents";
  const filename =
    filenameMatch?.[1] || `${fallbackName}_${new Date().toISOString().split("T")[0]}.vcf`;

  return { blob, filename };
}

/** Saves the blob under its filename via a transient anchor click. */
function saveBlob({ blob, filename }: DownloadedFile): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/** Exports the given rows as a .vcf file and saves it. */
export function useVCardExport(kind: VCardExportKind) {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (ids: string[]) => downloadVCard(kind, ids),
    onSuccess: (result) => {
      saveBlob(result);
      // No count: the parents file holds more cards than selected rows.
      toast.success(i18n._({ id: "vcardExport.toast.downloaded", message: "vCard file downloaded" }));
    },
    onError: (error: Error) => {
      console.error("vCard export error:", error);
      toast.error(i18n._({ id: "vcardExport.toast.error", message: "vCard export failed" }));
    },
  });
}
